import { useCallback, useEffect, useMemo } from 'react'

import { FontAwesomeIconKey, getIconKeyFromString } from 'govwell-ui/components/Icon/util'
import { RecordTypeFragment } from 'types/graphql'

import { routes, useLocation, useParams } from '@redwoodjs/router'

import { InsightsPageType } from 'src/components/InsightsCommon/types'
import {
  NavElement,
  NavGroup,
  NavGroupKey,
  NavItem,
} from 'src/components/Sidebar/Navigation/Navigation'
import { useStaffAIAssistant } from 'src/components/StaffAIAssistant/StaffAIAssistantContext'
import { OrgSlugs } from 'src/constants'
import { useGetUnansweredAIChatbotFAQsCountQuery } from 'src/fetch/aiChatbotFAQ'
import { useCanViewInsightsPage } from 'src/hooks/use-can-view-insights-page'
import useDisclosure from 'src/hooks/use-disclosure'
import { useIsInspectionSchedulingWithTimesEnabled } from 'src/hooks/use-is-inspection-scheduling-with-times-enabled'
import {
  useAmIAdmin,
  useAmIAIStaffMember,
  useAmIInspector,
  useAmIReviewer,
  useAppStateContext,
  useIsGovWellStaff,
  useMyOrg,
  useMyOrgAddressEntityLayerId,
  useMyOrgParcelEntityLayerId,
  useMyRecordTypes,
} from 'src/layouts/AppStateContextLayout/utils'
import { KeyboardKey } from 'src/types'
import {
  useIsInternalStaffAIEnabled,
  useIsRecordFormAutoCheckEnabled,
} from 'src/utils/featureFlagging/featureFlagging'

export const useStaffSidebarMainNavElements = (): NavElement[] => {
  const { pathname } = useLocation()
  const {
    addressUuid,
    identifier,
    parcelUuid,
    recordUuid,
    slug: paramOrgSlug,
    settlementUuid,
  } = useParams()

  const appState = useAppStateContext()
  const currentlyViewedRecordTypeId = appState?.currentlyViewedRecordTypeId

  const isAIAssistantActive = pathname.includes(routes.aiAssistantSettings())
  const recordTypes = useMyRecordTypes()

  const isReviewer = useAmIReviewer()
  const isInspector = useAmIInspector()
  const isAdmin = useAmIAdmin()
  const isAIStaffMember = useAmIAIStaffMember()

  const myOrg = useMyOrg()
  const myOrgSlug = myOrg?.slug
  const isHutto = myOrgSlug === OrgSlugs.Hutto
  const hasFinixMerchant = myOrg?.finixMerchants && myOrg?.finixMerchants?.length > 0

  const org = useMyOrg()
  const hasCodeEnforcementModule = org?.hasCodeEnforcementModule
  const showInsightsPage = useCanViewInsightsPage({ insightsPageType: InsightsPageType.Overview })

  const hasRecordTypes = !!recordTypes?.length

  const isGovWellStaff = useIsGovWellStaff()

  const isSchedulingWithTimesEnabled = useIsInspectionSchedulingWithTimesEnabled()
  const showPayments = recordTypes?.some((rt) => rt.allowFees) && !isReviewer

  const searchModalState = useDisclosure()

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === KeyboardKey.K) {
        searchModalState.open()
      }
    }
    window.addEventListener('keydown', listener)
    return () => window.removeEventListener('keydown', listener)
  }, [searchModalState])

  const checkIfPathEquals = useCallback((href: string) => pathname === href, [pathname])
  const checkIfPathIncludes = useCallback((href: string) => pathname.includes(href), [pathname])

  const shouldShowInsightsAutoCheckPage = useCanViewInsightsPage({
    insightsPageType: InsightsPageType.AutoCheck,
  })
  const shouldShowInsightsCommunityPage = useCanViewInsightsPage({
    insightsPageType: InsightsPageType.Community,
  })
  const shouldShowInsightsInspectionsPage = useCanViewInsightsPage({
    insightsPageType: InsightsPageType.Inspections,
  })

  const isAIChatEnabled = org?.isAIChatEnabled ?? false
  const shouldShowCommunityAssistantInsights = isAIChatEnabled

  const insightsNavGroup: NavGroup = useMemo(
    () => ({
      icon: 'FaChartLine',
      items: [
        {
          internalRoute: routes.insights(),
          isActive: checkIfPathEquals(routes.insights()),
          title: 'Overview',
        },
        ...(shouldShowCommunityAssistantInsights
          ? [
              {
                internalRoute: routes.communityAssistantInsights(),
                isActive: checkIfPathEquals(routes.communityAssistantInsights()),
                title: 'Assistant',
              },
            ]
          : []),
        ...(shouldShowInsightsAutoCheckPage
          ? [
              {
                internalRoute: routes.insightsAutoCheck(),
                isActive: checkIfPathEquals(routes.insightsAutoCheck()),
                title: 'AutoCheck',
              },
            ]
          : []),
        ...(shouldShowInsightsInspectionsPage
          ? [
              {
                internalRoute: routes.insightsInspections(),
                isActive: checkIfPathEquals(routes.insightsInspections()),
                title: 'Inspections',
              },
            ]
          : []),
        ...(shouldShowInsightsCommunityPage
          ? [
              {
                internalRoute: routes.insightsCommunity(),
                isActive: checkIfPathEquals(routes.insightsCommunity()),
                title: 'Community',
              },
            ]
          : []),
      ],
      key: NavGroupKey.Insights,
      title: 'Insights',
    }),
    [
      checkIfPathEquals,
      shouldShowInsightsAutoCheckPage,
      shouldShowCommunityAssistantInsights,
      shouldShowInsightsCommunityPage,
      shouldShowInsightsInspectionsPage,
    ]
  )

  const { count: unansweredAIChatbotFAQsCount } = useGetUnansweredAIChatbotFAQsCountQuery()
  const unansweredAIChatbotFAQsCountBadge =
    unansweredAIChatbotFAQsCount > 0 ? unansweredAIChatbotFAQsCount : undefined
  const aiAssistantNavGroup: NavGroup = useMemo(
    () => ({
      badge: unansweredAIChatbotFAQsCountBadge,
      icon: 'FaComment',
      items: [
        {
          badge: unansweredAIChatbotFAQsCountBadge,
          internalRoute: routes.aiAssistantTrain(),
          isActive: checkIfPathEquals(routes.aiAssistantTrain()),
          title: 'Train',
        },
        {
          internalRoute: routes.aiAssistantTest(),
          isActive: checkIfPathEquals(routes.aiAssistantTest()),
          title: 'Test',
        },
        {
          internalRoute: routes.aiAssistantHistory(),
          isActive: checkIfPathEquals(routes.aiAssistantHistory()),
          title: 'History',
        },
        {
          internalRoute: routes.aiAssistantRouting(),
          isActive: checkIfPathEquals(routes.aiAssistantRouting()),
          title: 'Routing',
        },
        {
          internalRoute: routes.aiAssistantSettings(),
          isActive: checkIfPathEquals(routes.aiAssistantSettings()),
          title: 'Settings',
        },
      ],
      key: NavGroupKey.AIAssistant,
      title: 'Community Assistant',
    }),
    [checkIfPathEquals, unansweredAIChatbotFAQsCountBadge]
  )
  const getIsRecordsItemActive = useCallback(
    (recordType: RecordTypeFragment) => {
      const recordTypeId = recordType.id
      const category = recordType.category

      const isOnRecordPage = recordUuid ? checkIfPathEquals(routes.record({ recordUuid })) : false

      if (category === 'Violations') {
        return (
          (isOnRecordPage && recordTypeId === currentlyViewedRecordTypeId) ||
          checkIfPathEquals(routes.recordsList({ recordTypeId })) ||
          (!!paramOrgSlug &&
            checkIfPathEquals(
              routes.submitComplaintRecordType({ recordTypeId, slug: paramOrgSlug })
            ))
        )
      }

      return (
        (isOnRecordPage && recordTypeId === currentlyViewedRecordTypeId) ||
        checkIfPathEquals(routes.recordsList({ recordTypeId })) ||
        (!!paramOrgSlug &&
          checkIfPathEquals(routes.recordTemplatePick({ recordTypeId, slug: paramOrgSlug })))
      )
    },
    [checkIfPathEquals, currentlyViewedRecordTypeId, paramOrgSlug, recordUuid]
  )

  const getIsPlanReviewsActiveForRecordType = useCallback(
    (recordType: RecordTypeFragment) => {
      const recordTypeId = recordType.id
      if (!recordTypeId) {
        return false
      }
      return checkIfPathEquals(routes.recordsPlanReviews({ recordTypeId }))
    },
    [checkIfPathEquals]
  )

  const getShouldShowInspectionsForRecordType = useCallback(
    (recordType: RecordTypeFragment) => {
      if (!recordType.allowInspections) {
        return false
      }
      if (isReviewer && !isInspector) {
        return false
      }
      return true
    },
    [isInspector, isReviewer]
  )

  const getIsInspectionsActiveForRecordType = useCallback(
    (recordType: RecordTypeFragment) => {
      const recordTypeId = recordType.id
      if (!recordTypeId) {
        return false
      }
      return checkIfPathEquals(routes.recordsInspections({ recordTypeId }))
    },
    [checkIfPathEquals]
  )

  const getIsDocumentsActiveForRecordType = useCallback(
    (recordType: RecordTypeFragment) => {
      const recordTypeId = recordType.id
      if (!recordTypeId) {
        return false
      }
      return checkIfPathEquals(routes.moduleDocuments({ recordTypeId }))
    },
    [checkIfPathEquals]
  )

  const recordTypeNavGroups: NavGroup[] = useMemo(() => {
    return recordTypes.map((rt) => ({
      icon: getIconKeyFromString((rt.icon ?? 'faSquare') as FontAwesomeIconKey),
      items: [
        ...(!isReviewer
          ? [
              {
                internalRoute: routes.recordsList({ recordTypeId: rt.id }),
                isActive: getIsRecordsItemActive(rt),
                title: rt.recordName ? `${rt.recordName}s` : 'Permits',
              },
            ]
          : []),
        ...(rt.allowPlanReviews
          ? [
              {
                isActive: getIsPlanReviewsActiveForRecordType(rt),
                internalRoute: routes.recordsPlanReviews({ recordTypeId: rt.id }),
                title: 'Plan Reviews',
              },
            ]
          : []),
        ...(getShouldShowInspectionsForRecordType(rt)
          ? [
              {
                isActive: getIsInspectionsActiveForRecordType(rt),
                internalRoute: routes.recordsInspections({ recordTypeId: rt.id }),
                title: 'Inspections',
              },
            ]
          : []),
        ...(!isReviewer
          ? [
              {
                isActive: getIsDocumentsActiveForRecordType(rt),
                internalRoute: routes.moduleDocuments({ recordTypeId: rt.id }),
                title: 'Documents',
              },
            ]
          : []),
      ],
      key: `${NavGroupKey.RecordType}-${rt.id}`,
      title: rt.name,
    }))
  }, [
    getIsDocumentsActiveForRecordType,
    getIsInspectionsActiveForRecordType,
    getIsPlanReviewsActiveForRecordType,
    getIsRecordsItemActive,
    getShouldShowInspectionsForRecordType,
    isReviewer,
    recordTypes,
  ])

  const isComplaintsActive =
    checkIfPathEquals(routes.codeComplaints()) ||
    checkIfPathEquals(routes.codeComplaint({ identifier: identifier ?? '' })) ||
    (paramOrgSlug ? checkIfPathEquals(routes.submitComplaint({ slug: paramOrgSlug })) : false)

  const codeNavGroup: NavGroup = useMemo(
    () => ({
      icon: 'FaBrakeWarning',
      items: [
        {
          internalRoute: routes.codeComplaints(),
          isActive: isComplaintsActive,
          title: 'Cases',
        },
        {
          internalRoute: routes.codeInspections(),
          isActive: checkIfPathEquals(routes.codeInspections()),
          title: 'Inspections',
        },
      ],
      key: NavGroupKey.CodeEnforcement,
      title: 'Code Enforcement',
    }),
    [checkIfPathEquals, isComplaintsActive]
  )

  const addressEntityLayerId = useMyOrgAddressEntityLayerId()
  const parcelEntityLayerId = useMyOrgParcelEntityLayerId()
  const isOnAddressPage = addressUuid
    ? checkIfPathEquals(routes.entityAddress({ addressUuid }))
    : false
  const isOnParcelPage = parcelUuid ? checkIfPathEquals(routes.entityParcel({ parcelUuid })) : false
  const locationsNavElement: NavElement | null = useMemo(() => {
    const addressItem: NavItem = {
      internalRoute: routes.staffAddresses(),
      isActive: checkIfPathIncludes(routes.staffAddresses()) || isOnAddressPage,
      title: 'Addresses',
    }
    const parcelItem: NavItem = {
      internalRoute: routes.staffParcels(),
      isActive: checkIfPathIncludes(routes.staffParcels()) || isOnParcelPage,
      title: 'Parcels',
    }
    if (addressEntityLayerId && parcelEntityLayerId) {
      return {
        icon: 'FaMapPin',
        items: [addressItem, parcelItem],
        key: NavGroupKey.Locations,
        title: 'Locations',
      }
    }
    if (addressEntityLayerId) {
      return {
        ...addressItem,
        icon: 'FaMapPin',
      }
    }
    if (parcelEntityLayerId) {
      return {
        ...parcelItem,
        icon: 'FaMapPin',
      }
    }
    return null
  }, [
    addressEntityLayerId,
    checkIfPathIncludes,
    isOnAddressPage,
    isOnParcelPage,
    parcelEntityLayerId,
  ])

  const recordTaskItem: NavItem | null = useMemo(
    () =>
      hasRecordTypes
        ? {
            icon: 'FaListCheck',
            internalRoute: routes.tasks(),
            isActive: checkIfPathEquals(routes.tasks()),
            title: 'Tasks',
          }
        : null,
    [checkIfPathEquals, hasRecordTypes]
  )

  const meetingsNavItem: NavItem | null = useMemo(
    () =>
      myOrg?.hasRecordTypeWithMeetingsEnabled
        ? {
            icon: 'FaBriefcase',
            internalRoute: routes.organizationMeetings(),
            isActive: checkIfPathEquals(routes.organizationMeetings()),
            title: 'Meetings',
          }
        : null,
    [checkIfPathEquals, myOrg?.hasRecordTypeWithMeetingsEnabled]
  )

  const showSettlements = hasFinixMerchant
  const paymentsNavGroup: NavGroup = useMemo(() => {
    const items: NavItem[] = [
      {
        internalRoute: routes.paymentTransfers(),
        isActive: checkIfPathEquals(routes.paymentTransfers()),
        title: 'Transactions',
      },
    ]
    if (showSettlements) {
      items.push({
        internalRoute: routes.settlements(),
        isActive:
          checkIfPathEquals(routes.settlements()) ||
          (!!settlementUuid && checkIfPathEquals(routes.settlement({ settlementUuid }))),
        title: 'Settlements',
      })
    }
    items.push(
      {
        internalRoute: routes.recordTaskPayments(),
        isActive: checkIfPathEquals(routes.recordTaskPayments()),
        title: 'Invoices',
      },
      {
        internalRoute: routes.recordTaskFees(),
        isActive: checkIfPathEquals(routes.recordTaskFees()),
        title: 'Fees',
      }
    )
    return {
      icon: 'FaDollar',
      items,
      key: NavGroupKey.Payments,
      title: 'Payments',
    }
  }, [checkIfPathEquals, showSettlements])

  const inspectionCalendarNavItem: NavItem | null = useMemo(
    () =>
      isSchedulingWithTimesEnabled
        ? {
            icon: 'FaCalendar',
            internalRoute: routes.calendar(),
            isActive: checkIfPathEquals(routes.calendar()),
            title: 'Inspection Calendar',
          }
        : null,
    [checkIfPathEquals, isSchedulingWithTimesEnabled]
  )

  const reportingNavItem: NavItem | null = useMemo(
    () =>
      isAdmin
        ? {
            icon: 'FaLineChart',
            internalRoute: routes.reporting(),
            isActive: checkIfPathEquals(routes.reporting()),
            title: 'Reporting',
          }
        : null,
    [checkIfPathEquals, isAdmin]
  )

  const contactsNavItem: NavItem | null = useMemo(
    () =>
      isHutto
        ? {
            icon: 'FACircleUser',
            internalRoute: routes.contacts(),
            isActive: checkIfPathEquals(routes.contacts()),
            title: 'Contacts',
          }
        : null,
    [checkIfPathEquals, isHutto]
  )

  const govWellStaffNavItems: NavItem[] | null = useMemo(() => {
    if (!isGovWellStaff) {
      return null
    }
    const singleOrgManagementPageRoute = paramOrgSlug
      ? routes.adminOrg({ slug: paramOrgSlug })
      : null

    return [
      {
        icon: 'FaUsers',
        internalRoute: routes.adminOrgs(),
        isActive:
          checkIfPathEquals(routes.adminOrgs()) ||
          (!!singleOrgManagementPageRoute && checkIfPathEquals(singleOrgManagementPageRoute)),
        title: 'Org Management',
      },
      {
        icon: 'FaUsersRectangle',
        internalRoute: routes.adminUsers(),
        isActive: checkIfPathEquals(routes.adminUsers()),
        title: 'User Management',
      },
    ]
  }, [checkIfPathEquals, isGovWellStaff, paramOrgSlug])

  const moreUseCasesNavItem: NavItem = useMemo(
    () => ({
      icon: 'FaPlus',
      internalRoute: routes.useCases(),
      isActive: checkIfPathEquals(routes.useCases()),
      title: 'More Use Cases',
    }),
    [checkIfPathEquals]
  )

  const recordFormAutoCheckNavItem: NavItem = useMemo(
    () => ({
      icon: 'FaCheck',
      internalRoute: routes.recordFormAutoCheck(),
      isActive: checkIfPathEquals(routes.recordFormAutoCheck()),
      title: 'Record Form AutoCheck',
    }),
    [checkIfPathEquals]
  )

  const isRecordFormAutoCheckEnabled = useIsRecordFormAutoCheckEnabled()
  const isInternalStaffAIEnabled = useIsInternalStaffAIEnabled()
  const staffAIAssistantContext = useStaffAIAssistant()

  const staffAIAssistantNavItem: NavItem | null = useMemo(
    () =>
      staffAIAssistantContext
        ? {
            icon: 'FaSparkles',
            onClick: (e: React.MouseEvent | React.KeyboardEvent) => {
              e.preventDefault()
              e.stopPropagation()
              staffAIAssistantContext.overlay.open()
            },
            isActive: false,
            isHighlighted: true,
            title: 'AI Assistant',
          }
        : null,
    [staffAIAssistantContext]
  )

  const navElements: NavElement[] = useMemo(
    () => [
      ...(isInternalStaffAIEnabled && staffAIAssistantNavItem ? [staffAIAssistantNavItem] : []),
      ...(showInsightsPage ? [insightsNavGroup] : []),
      ...(isAIStaffMember || isAIAssistantActive ? [aiAssistantNavGroup] : []),
      ...(isRecordFormAutoCheckEnabled ? [recordFormAutoCheckNavItem] : []),
      ...recordTypeNavGroups,
      ...(hasCodeEnforcementModule && !isReviewer ? [codeNavGroup] : []),
      ...(locationsNavElement ? [locationsNavElement] : []),
      ...(recordTaskItem ? [recordTaskItem] : []),
      ...(meetingsNavItem ? [meetingsNavItem] : []),
      ...(showPayments ? [paymentsNavGroup] : []),
      ...(inspectionCalendarNavItem ? [inspectionCalendarNavItem] : []),
      ...(reportingNavItem ? [reportingNavItem] : []),
      ...(contactsNavItem ? [contactsNavItem] : []),
      ...(govWellStaffNavItems ? govWellStaffNavItems : []),
      moreUseCasesNavItem,
    ],
    [
      aiAssistantNavGroup,
      codeNavGroup,
      contactsNavItem,
      govWellStaffNavItems,
      hasCodeEnforcementModule,
      insightsNavGroup,
      inspectionCalendarNavItem,
      isAIAssistantActive,
      isAIStaffMember,
      isInternalStaffAIEnabled,
      isRecordFormAutoCheckEnabled,
      isReviewer,
      locationsNavElement,
      meetingsNavItem,
      moreUseCasesNavItem,
      paymentsNavGroup,
      recordFormAutoCheckNavItem,
      recordTaskItem,
      recordTypeNavGroups,
      reportingNavItem,
      showInsightsPage,
      showPayments,
      staffAIAssistantNavItem,
    ]
  )

  return navElements
}
